/**
 * Member Profile Creation Tab (Tab 1)
 * Upload insurance cards (SFHP health plan + CA state ID) -> OCR extraction -> member matching/creation
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getWeaveinApiUrl } from '@/src/config';
import { programsAPI, programBillsPayer } from '@/services/api/programs';
import {
  FileText,
  Upload,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  ArrowLeft,
  Clock,
  Loader2,
  ScanLine,
  User,
  UserPlus,
  CreditCard,
  Shield,
  Zap,
  Trash2,
  Link2,
  AlertTriangle,
  Save,
} from 'lucide-react';
import documentProcessingApi, {
  type InsuranceDocument,
  type InsuranceDocStatus,
  type InsuranceDocFilters,
  type MemberMatchRequest,
  type ExtractedField,
  type InsuranceDocFieldUpdate,
} from '@/services/api/documentProcessing';

interface MemberProfileTabProps {
  onNavigate?: (tab: string) => void;
}

const DOC_STATUS_CONFIG: Record<InsuranceDocStatus, { label: string; color: string; icon: React.ReactNode }> = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: <Clock className="w-3 h-3" /> },
  extracted: { label: 'Extracted', color: 'bg-yellow-100 text-yellow-700', icon: <ScanLine className="w-3 h-3" /> },
  matched: { label: 'Matched', color: 'bg-blue-100 text-blue-700', icon: <Link2 className="w-3 h-3" /> },
  profile_created: { label: 'Profile Created', color: 'bg-green-100 text-green-700', icon: <CheckCircle className="w-3 h-3" /> },
};

const CARD_TYPE_LABELS: Record<string, string> = {
  sfhp: 'SFHP Health Plan',
  medi_cal: 'Medi-Cal',
  state_id: 'State ID',
  drivers_license: "Driver's License",
  anthem: 'Anthem Blue Cross',
  healthnet: 'Health Net',
  kaiser: 'Kaiser Permanente',
  combined: 'Combined',
};

const CATEGORY_LABELS: Record<string, { label: string; icon: React.ReactNode }> = {
  health_plan_card: { label: 'Health Plan Card', icon: <CreditCard className="w-4 h-4" /> },
  government_id: { label: 'Government ID', icon: <Shield className="w-4 h-4" /> },
  combined: { label: 'Combined Cards', icon: <ScanLine className="w-4 h-4" /> },
};

/** Convert YYYY-MM-DD to MM/DD/YYYY for display */
function toDisplayDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  // Already in MM/DD/YYYY format
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) return dateStr;
  // YYYY-MM-DD format
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  return dateStr;
}

/** Convert MM/DD/YYYY back to YYYY-MM-DD for API/DB storage */
function toStorageDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  // Already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  // MM/DD/YYYY format
  const m = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  return dateStr;
}

export default function MemberProfileTab({ onNavigate }: MemberProfileTabProps) {
  // View state
  const [view, setView] = useState<'list' | 'detail'>('list');

  // List state
  const [items, setItems] = useState<InsuranceDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Detail state
  const [selectedDoc, setSelectedDoc] = useState<InsuranceDocument | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Editable fields state
  const [editableFields, setEditableFields] = useState<InsuranceDocFieldUpdate>({});
  const [saving, setSaving] = useState(false);

  // Member matching state
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [memberSearchResults, setMemberSearchResults] = useState<Array<{ id: number; name: string; medicaid_id?: string }>>([]);
  const [memberSearching, setMemberSearching] = useState(false);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newMemberForm, setNewMemberForm] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    medicaid_id: '',
  });

  // Member creation confirmation
  const [memberCreated, setMemberCreated] = useState<{ name: string } | null>(null);
  const creatingMemberRef = useRef(false); // Ref guard to prevent double-click race condition

  // Upload
  const [uploading, setUploading] = useState(false);

  // Build authenticated URL for iframe/img src (browser requests can't send Authorization header)
  const getAuthenticatedUrl = (storageUrl: string) => {
    const base = getWeaveinApiUrl();
    // If URL already has a token param (embedded by backend GET endpoint), use as-is
    if (storageUrl.includes('token=')) return `${base}${storageUrl}`;
    const token = localStorage.getItem('auth_token') || '';
    const cboId = localStorage.getItem('active_cbo_id') || '';
    const sep = storageUrl.includes('?') ? '&' : '?';
    return `${base}${storageUrl}${sep}token=${encodeURIComponent(token)}&cbo_id=${encodeURIComponent(cboId)}`;
  };
  const healthPlanFileRef = useRef<HTMLInputElement>(null);

  // TRUS-725: a health-plan card used to be uploaded as card_type 'sfhp' for every org,
  // which runs SFHP's OCR template over a Health Net or L.A. Care card. Now: 'sfhp' only
  // when the org's chosen plans include SFHP (or no plan is chosen yet — unchanged from
  // before); otherwise no card_type, and the server detects the plan from the card text.
  const [healthPlanCardType, setHealthPlanCardType] = useState<string>('sfhp');
  useEffect(() => {
    let cancelled = false;
    programsAPI
      .listPrograms({ is_active: true })
      .then((list) => {
        if (cancelled) return;
        const withPlan = (list || []).filter((p) => p.payer_key);
        if (withPlan.length > 0 && !withPlan.some((p) => programBillsPayer(p, 'SFHP'))) {
          setHealthPlanCardType('');
        }
      })
      .catch(() => {
        /* keep the previous behaviour */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const govIdFileRef = useRef<HTMLInputElement>(null);
  const combinedFileRef = useRef<HTMLInputElement>(null);

  // ---- Data Loading ----

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const filters: InsuranceDocFilters = { limit: 50 };
      if (statusFilter) filters.status = statusFilter;
      const resp = await documentProcessingApi.listInsuranceCards(filters);
      setItems(resp.items);
      setTotal(resp.total);
    } catch (err) {
      console.error('Failed to load insurance cards:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const loadDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try {
      const data = await documentProcessingApi.getInsuranceCard(id);
      setSelectedDoc(data);
      // Initialize editable fields from extracted data
      setEditableFields({
        member_cin: data.member_cin || '',
        member_plan_id: data.member_plan_id || '',
        plan_name: data.plan_name || '',
        extracted_first_name: data.extracted_first_name || '',
        extracted_last_name: data.extracted_last_name || '',
        extracted_dob: toDisplayDate(data.extracted_dob),
        coverage_effective_date: toDisplayDate(data.coverage_effective_date),
        language: data.language || '',
      });
      // Pre-fill new member form from extracted fields
      // Note: date_of_birth stays YYYY-MM-DD for the native date input (type="date")
      setNewMemberForm({
        first_name: data.extracted_first_name || '',
        last_name: data.extracted_last_name || '',
        date_of_birth: data.extracted_dob || '',
        medicaid_id: data.member_cin || '',
      });
    } catch (err) {
      console.error('Failed to load insurance card detail:', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // ---- Actions ----

  const handleUpload = async (file: File, documentCategory: string, cardType: string) => {
    setUploading(true);
    try {
      const doc = await documentProcessingApi.uploadInsuranceCard(file, cardType, documentCategory);
      // Auto-process
      try {
        await documentProcessingApi.processInsuranceCard(doc.id);
      } catch {
        // OCR may not be configured
      }
      loadList();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleHealthPlanUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleUpload(file, 'health_plan_card', healthPlanCardType);
    if (healthPlanFileRef.current) healthPlanFileRef.current.value = '';
  };

  const handleGovIdUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleUpload(file, 'government_id', 'state_id');
    if (govIdFileRef.current) govIdFileRef.current.value = '';
  };

  const handleCombinedUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleUpload(file, 'combined', 'combined');
    if (combinedFileRef.current) combinedFileRef.current.value = '';
  };

  const handleProcess = async (id: number) => {
    setActionLoading(true);
    try {
      await documentProcessingApi.processInsuranceCard(id);
      loadDetail(id);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Processing failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMatchExistingMember = async (memberId: number) => {
    if (!selectedDoc) return;
    setActionLoading(true);
    try {
      const result = await documentProcessingApi.matchMemberToCard(selectedDoc.id, {
        member_id: memberId,
      });
      // Backend returns the updated document dict (not { success: true, ... })
      if (result.member_id) {
        loadDetail(selectedDoc.id);
        loadList();
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Member matching failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateNewMember = async () => {
    if (!selectedDoc) return;
    if (memberCreated) return; // Prevent duplicate creation (state guard)
    if (creatingMemberRef.current) return; // Prevent double-click race condition (ref guard)
    if (selectedDoc.member_id) return; // Already has a linked member
    if (!newMemberForm.first_name.trim() || !newMemberForm.last_name.trim()) {
      alert('First name and last name are required');
      return;
    }
    creatingMemberRef.current = true;
    setActionLoading(true);
    try {
      const result = await documentProcessingApi.matchMemberToCard(selectedDoc.id, {
        create_new: {
          first_name: newMemberForm.first_name,
          last_name: newMemberForm.last_name,
          date_of_birth: toStorageDate(newMemberForm.date_of_birth) || undefined,
          medicaid_id: newMemberForm.medicaid_id || undefined,
        },
      });
      // Backend returns the updated document dict (not { success: true, ... })
      // so check for member_id to confirm creation succeeded
      if (result.member_id) {
        const fullName = `${newMemberForm.first_name} ${newMemberForm.last_name}`;
        setMemberCreated({ name: fullName });
        setShowCreateNew(false);
        // Update selectedDoc locally to show confirmation immediately
        // (don't call loadDetail — it shows a spinner that hides the confirmation banner)
        setSelectedDoc(prev => prev ? {
          ...prev,
          member_id: result.member_id,
          processing_status: 'profile_created' as InsuranceDocStatus,
          member_name: result.member_name || fullName,
          match_method: result.match_method || 'new_profile_created',
        } : prev);
        loadList();
      }
    } catch (err: any) {
      creatingMemberRef.current = false; // Allow retry on error
      alert(err.response?.data?.detail || 'Member creation failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this insurance document?')) return;
    setActionLoading(true);
    try {
      await documentProcessingApi.deleteInsuranceCard(id);
      if (view === 'detail') {
        backToList();
      } else {
        loadList();
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Delete failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveFields = async () => {
    if (!selectedDoc) return;
    setSaving(true);
    try {
      // Convert display dates (MM/DD/YYYY) back to storage format (YYYY-MM-DD) for API
      const payload = {
        ...editableFields,
        extracted_dob: toStorageDate(editableFields.extracted_dob),
        coverage_effective_date: toStorageDate(editableFields.coverage_effective_date),
      };
      const updated = await documentProcessingApi.updateInsuranceCardFields(selectedDoc.id, payload);
      setSelectedDoc(updated);
      // Also update the new member form with saved values
      // Note: date_of_birth stays YYYY-MM-DD for the native date input (type="date")
      setNewMemberForm({
        first_name: updated.extracted_first_name || '',
        last_name: updated.extracted_last_name || '',
        date_of_birth: updated.extracted_dob || '',
        medicaid_id: updated.member_cin || '',
      });
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = (id: number) => {
    loadDetail(id);
    setView('detail');
  };

  const backToList = () => {
    setView('list');
    setSelectedDoc(null);
    setMemberSearchQuery('');
    setMemberSearchResults([]);
    setShowCreateNew(false);
    setMemberCreated(null);
    creatingMemberRef.current = false;
    loadList();
  };

  // ---- List View ----

  const renderList = () => {
    const statusTabs = [
      { value: '', label: 'All' },
      { value: 'pending', label: 'Pending' },
      { value: 'extracted', label: 'Extracted' },
      { value: 'matched', label: 'Matched' },
      { value: 'profile_created', label: 'Profile Created' },
    ];

    return (
      <>
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            {statusTabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setStatusFilter(tab.value)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  statusFilter === tab.value
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => loadList()}>
              <RefreshCw className="w-4 h-4" />
            </Button>

            <div className="relative">
              <Button
                size="sm"
                variant="outline"
                onClick={() => healthPlanFileRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
                Upload Health Plan Card
              </Button>
              <input
                ref={healthPlanFileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.tiff,.bmp"
                onChange={handleHealthPlanUpload}
                className="hidden"
              />
            </div>

            <div className="relative">
              <Button
                size="sm"
                onClick={() => govIdFileRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Shield className="w-4 h-4 mr-1" />}
                Upload Government ID
              </Button>
              <input
                ref={govIdFileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.tiff,.bmp"
                onChange={handleGovIdUpload}
                className="hidden"
              />
            </div>

            <div className="relative">
              <Button
                size="sm"
                variant="outline"
                onClick={() => combinedFileRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ScanLine className="w-4 h-4 mr-1" />}
                Upload Combined Cards
              </Button>
              <input
                ref={combinedFileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.tiff,.bmp"
                onChange={handleCombinedUpload}
                className="hidden"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium text-gray-600">Document</th>
                  <th className="text-left p-3 font-medium text-gray-600">Category</th>
                  <th className="text-left p-3 font-medium text-gray-600">Card Type</th>
                  <th className="text-left p-3 font-medium text-gray-600">Status</th>
                  <th className="text-left p-3 font-medium text-gray-600">Extracted Info</th>
                  <th className="text-left p-3 font-medium text-gray-600">Matched Member</th>
                  <th className="text-left p-3 font-medium text-gray-600">Date</th>
                  <th className="text-left p-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center p-8 text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                      Loading insurance documents...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center p-8 text-gray-500">
                      <CreditCard className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      No insurance documents found. Upload a health plan card or government ID to get started.
                    </td>
                  </tr>
                ) : (
                  items.map((doc) => {
                    const statusCfg = DOC_STATUS_CONFIG[doc.processing_status] || DOC_STATUS_CONFIG.pending;
                    const categoryCfg = CATEGORY_LABELS[doc.document_category] || { label: doc.document_category, icon: <FileText className="w-4 h-4" /> };

                    return (
                      <tr
                        key={doc.id}
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => openDetail(doc.id)}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {categoryCfg.icon}
                            <span className="font-medium text-gray-900 truncate max-w-[180px]">
                              {doc.source_file_name || `Card #${doc.id}`}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-gray-600">{categoryCfg.label}</span>
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-gray-600">
                            {doc.card_type ? (CARD_TYPE_LABELS[doc.card_type] || doc.card_type) : '--'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
                            {statusCfg.icon}
                            {statusCfg.label}
                          </span>
                        </td>
                        <td className="p-3">
                          {doc.member_cin || doc.extracted_last_name ? (
                            <div className="text-xs">
                              {doc.member_cin && <div>CIN: {doc.member_cin}</div>}
                              {doc.extracted_last_name && (
                                <div>{doc.extracted_first_name} {doc.extracted_last_name}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">--</span>
                          )}
                        </td>
                        <td className="p-3">
                          {doc.member_name ? (
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3 text-green-500" />
                              <span className="text-sm">{doc.member_name}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Not matched</span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-gray-500">
                            {new Date(doc.created_at).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                openDetail(doc.id);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                handleDelete(doc.id);
                              }}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {total > items.length && (
            <div className="p-3 border-t text-center text-sm text-gray-500">
              Showing {items.length} of {total} documents
            </div>
          )}
        </Card>
      </>
    );
  };

  // ---- Detail View ----

  const renderDetail = () => {
    if (detailLoading || !selectedDoc) {
      return (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      );
    }

    const statusCfg = DOC_STATUS_CONFIG[selectedDoc.processing_status] || DOC_STATUS_CONFIG.pending;
    const categoryCfg = CATEGORY_LABELS[selectedDoc.document_category] || { label: selectedDoc.document_category, icon: <FileText className="w-4 h-4" /> };
    const canProcess = true; // Always allow re-processing; backend resets queue state
    const canMatch = selectedDoc.processing_status === 'extracted' && !selectedDoc.member_id;
    const isMatched = selectedDoc.processing_status === 'matched' || selectedDoc.processing_status === 'profile_created';

    return (
      <>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={backToList}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <h2 className="text-lg font-semibold">
              {selectedDoc.source_file_name || `Card #${selectedDoc.id}`}
            </h2>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
              {statusCfg.icon}
              {statusCfg.label}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {canProcess && (
              <Button size="sm" onClick={() => handleProcess(selectedDoc.id)} disabled={actionLoading}>
                {actionLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                {selectedDoc.processing_status === 'pending' ? 'Process OCR' : 'Re-process OCR'}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDelete(selectedDoc.id)}
              disabled={actionLoading}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </div>
        </div>

        {/* Two-panel layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" style={{ minHeight: '500px' }}>
          {/* Left: Document Viewer */}
          <Card className="p-0 overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b flex items-center gap-2">
              {categoryCfg.icon}
              <span className="text-sm font-medium text-gray-700">{categoryCfg.label}</span>
              {selectedDoc.card_type && (
                <span className="text-xs text-gray-500 ml-1">
                  ({CARD_TYPE_LABELS[selectedDoc.card_type] || selectedDoc.card_type})
                </span>
              )}
            </div>
            <div className="h-[500px]">
              {selectedDoc.storage_url ? (
                selectedDoc.mime_type === 'application/pdf' ? (
                  <iframe
                    src={getAuthenticatedUrl(selectedDoc.storage_url)}
                    className="w-full h-full border-0"
                    title="Insurance Card"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full bg-gray-100 p-4">
                    <img
                      src={getAuthenticatedUrl(selectedDoc.storage_url)}
                      alt="Insurance Card"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                )
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <CreditCard className="w-12 h-12 mx-auto mb-2" />
                    <p>No document preview available</p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Right: Extracted Fields + Member Matching */}
          <div className="space-y-4">
            {/* Extracted Fields Card — Editable */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Extracted Information</h3>
                <Button size="sm" onClick={handleSaveFields} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Changes
                </Button>
              </div>
              <div className="space-y-2">
                {[
                  { key: 'member_cin' as const, label: 'CIN (Client ID)' },
                  { key: 'member_plan_id' as const, label: 'Member ID' },
                  { key: 'plan_name' as const, label: 'Plan Name' },
                  { key: 'extracted_first_name' as const, label: 'First Name' },
                  { key: 'extracted_last_name' as const, label: 'Last Name' },
                  { key: 'extracted_dob' as const, label: 'Date of Birth' },
                  { key: 'coverage_effective_date' as const, label: 'Coverage Effective Date' },
                  { key: 'language' as const, label: 'Language' },
                ].map((field) => (
                  <div key={field.key} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <span className="text-xs font-medium text-gray-600 w-1/3 shrink-0">{field.label}</span>
                    <Input
                      className="h-7 text-sm"
                      value={editableFields[field.key] || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setEditableFields(prev => ({ ...prev, [field.key]: e.target.value }))
                      }
                      placeholder="--"
                    />
                  </div>
                ))}
              </div>

              <p className="text-xs text-gray-400 mt-2 italic">
                Corrections improve future OCR accuracy for similar cards.
              </p>

              {selectedDoc.extraction_result && (
                <div className="mt-3 pt-3 border-t flex items-center gap-4 text-xs text-gray-500">
                  <span>Confidence: {Math.round(selectedDoc.extraction_result.overall_confidence * 100)}%</span>
                  <span>Fields: {selectedDoc.extraction_result.total_fields}</span>
                </div>
              )}
            </Card>

            {/* Member Match Section */}
            {isMatched && selectedDoc.member_name && (
              <Card className="p-4 bg-green-50 border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Member Matched</p>
                    <p className="text-sm text-green-600">
                      {selectedDoc.member_name}
                      {selectedDoc.match_method && (
                        <span className="ml-2 text-xs">({selectedDoc.match_method})</span>
                      )}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {memberCreated && (
              <Card className="p-4 bg-green-50 border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Member Profile Created Successfully</p>
                    <p className="text-sm text-green-600">
                      {memberCreated.name} has been created and linked to this insurance document.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {canMatch && !memberCreated && (
              <Card className="p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Match to Member</h3>

                {/* Search existing members */}
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search members by name or ID..."
                      value={memberSearchQuery}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMemberSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  {memberSearchResults.length > 0 && (
                    <div className="border rounded-md max-h-40 overflow-y-auto">
                      {memberSearchResults.map((m) => (
                        <button
                          key={m.id}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between text-sm border-b last:border-b-0"
                          onClick={() => handleMatchExistingMember(m.id)}
                          disabled={actionLoading}
                        >
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span>{m.name}</span>
                          </div>
                          {m.medicaid_id && (
                            <span className="text-xs text-gray-500">CIN: {m.medicaid_id}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <div className="flex-1 border-t border-gray-200" />
                    <span className="text-xs text-gray-400">or</span>
                    <div className="flex-1 border-t border-gray-200" />
                  </div>

                  {!showCreateNew ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setShowCreateNew(true)}
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Create New Member Profile
                    </Button>
                  ) : (
                    <div className="border rounded-md p-3 space-y-3 bg-blue-50">
                      <h4 className="text-sm font-medium text-gray-700">New Member Profile</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-gray-500">First Name *</label>
                          <Input
                            value={newMemberForm.first_name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setNewMemberForm((f) => ({ ...f, first_name: e.target.value }))
                            }
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Last Name *</label>
                          <Input
                            value={newMemberForm.last_name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setNewMemberForm((f) => ({ ...f, last_name: e.target.value }))
                            }
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Date of Birth</label>
                          <Input
                            type="date"
                            value={newMemberForm.date_of_birth}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setNewMemberForm((f) => ({ ...f, date_of_birth: e.target.value }))
                            }
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Medicaid ID (CIN)</label>
                          <Input
                            value={newMemberForm.medicaid_id}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              setNewMemberForm((f) => ({ ...f, medicaid_id: e.target.value }))
                            }
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleCreateNewMember}
                          disabled={actionLoading || !!memberCreated}
                          className="flex-1"
                        >
                          {actionLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
                          {memberCreated ? 'Member Created' : 'Create & Link Member'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowCreateNew(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {selectedDoc.processing_status === 'pending' && (
              <Card className="p-4 bg-yellow-50 border-yellow-200">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  <div>
                    <p className="font-medium text-yellow-800">Pending OCR Processing</p>
                    <p className="text-sm text-yellow-600">
                      Click "Process OCR" to extract information from this card.
                    </p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </>
    );
  };

  // ---- Main Render ----

  return (
    <div>
      {view === 'list' ? renderList() : renderDetail()}
    </div>
  );
}
